/**
 * Connect Page - Trade Loop v2.0
 *
 * Handles QR code / link connections:
 * - If user is logged in: auto-connect with partner and redirect to network
 * - If user is not logged in: redirect to signup with referral param
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  Loader2,
  UserPlus,
  CheckCircle,
  XCircle,
  LogIn,
  UserCircle,
} from "lucide-react";

const ConnectPage = () => {
  const { userId: partnerId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [connectionStatus, setConnectionStatus] = useState<
    "loading" | "connecting" | "success" | "error" | "self" | "guest"
  >("loading");

  // Fetch partner info
  const { data: partnerData, isPending: partnerLoading } = useQuery({
    queryKey: ["partner", partnerId],
    queryFn: async () => {
      const response = await api.get(`/api/users/${partnerId}/connection-preview`);

      // Response structure: { success: true, data: { id, fullName, companyName, trade, profileImageUrl } }
      const profile = response.data || response;

      if (!profile || !profile.id) {
        throw new Error('Invalid profile data received');
      }

      return {
        id: profile.id,
        fullName: profile.fullName || 'A Trade Loop Partner',
        trade: profile.trade,
        company: profile.companyName,
        profileImageUrl: profile.profileImageUrl,
      };
    },
    enabled: !!partnerId,
  });

  // Connect with partner
  const connectMutation = useMutation({
    mutationFn: async () => {
      return api.post("/api/workflows/connections/instant-connect", {
        partnerId: partnerId!
      });
    },
    onSuccess: () => {
      setConnectionStatus("success");
      showSuccess("Connected!", `You are now connected with ${partnerData?.fullName || "your new partner"}`);
      setTimeout(() => navigate("/network"), 2000);
    },
    onError: (error: any) => {
      // Extract detailed error message from backend
      const errorMessage = error.data?.error || error.message || "";

      if (errorMessage.includes("already") || errorMessage.includes("Already connected")) {
        setConnectionStatus("success");
        showSuccess("Already Connected", "You are already connected with this partner");
        setTimeout(() => navigate("/network"), 2000);
      } else {
        setConnectionStatus("error");
        const errorDetails = error.data?.details || "";
        showError("Connection Failed", errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage || "Could not connect with this user");

        // Log full error for debugging
        console.error("[ConnectPage] Connection request failed:", {
          partnerId,
          error,
          errorData: error.data,
          status: error.status
        });
      }
    },
  });

  // Handle connection logic
  useEffect(() => {
    if (authLoading || partnerLoading) return;

    // Not logged in - show guest options
    if (!isAuthenticated) {
      setConnectionStatus("guest");
      return;
    }

    // Can't connect with yourself
    if (user?.id === partnerId) {
      setConnectionStatus("self");
      return;
    }

    // Auto-connect for logged in users
    setConnectionStatus("connecting");
    connectMutation.mutate();
  }, [authLoading, partnerLoading, isAuthenticated, user?.id, partnerId]);

  // Handle signup redirect
  const handleSignup = () => {
    navigate(`/login?mode=signup&ref=${partnerId}`);
  };

  // Handle login redirect
  const handleLogin = () => {
    navigate(`/login?redirect=/connect/${partnerId}`);
  };

  // Loading state
  if (authLoading || partnerLoading || connectionStatus === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-80">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Connecting state
  if (connectionStatus === "connecting") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-80">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
            <p className="font-medium mb-1">Connecting...</p>
            <p className="text-sm text-muted-foreground">
              Setting up your partnership
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (connectionStatus === "success") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-80">
          <CardContent className="flex flex-col items-center py-12">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <p className="font-medium mb-1">Connected!</p>
            <p className="text-sm text-muted-foreground text-center">
              You can now send referrals to each other
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Self-connect error
  if (connectionStatus === "self") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-80">
          <CardContent className="flex flex-col items-center py-12">
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
              <UserCircle className="h-8 w-8 text-yellow-600" />
            </div>
            <p className="font-medium mb-1">That's You!</p>
            <p className="text-sm text-muted-foreground text-center mb-4">
              You can't connect with yourself
            </p>
            <Button onClick={() => navigate("/qr")}>
              Share Your QR Code
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (connectionStatus === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-80">
          <CardContent className="flex flex-col items-center py-12">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <p className="font-medium mb-1">Connection Failed</p>
            <p className="text-sm text-muted-foreground text-center mb-4">
              Something went wrong. Please try again.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/network")}>
                Go to Network
              </Button>
              <Button onClick={() => connectMutation.mutate()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Guest state - not logged in
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="flex flex-col items-center py-8">
          {/* Partner Info */}
          {partnerData?.profileImageUrl ? (
            <img
              src={partnerData.profileImageUrl}
              alt={partnerData.fullName}
              className="w-20 h-20 rounded-full object-cover mb-4"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <UserPlus className="h-10 w-10 text-primary" />
            </div>
          )}

          <h1 className="text-xl font-bold mb-1">Partner Invitation</h1>

          {partnerData && (
            <div className="text-center mb-6">
              <p className="font-medium">{partnerData.fullName}</p>
              {partnerData.trade && (
                <p className="text-sm text-muted-foreground">{partnerData.trade}</p>
              )}
              {partnerData.company && (
                <p className="text-sm text-muted-foreground">{partnerData.company}</p>
              )}
            </div>
          )}

          <p className="text-sm text-muted-foreground text-center mb-6">
            wants to connect with you on Trade Loop to share referrals and grow your business together.
          </p>

          {/* Actions */}
          <div className="w-full space-y-3">
            <Button className="w-full" size="lg" onClick={handleSignup}>
              <UserPlus className="h-4 w-4 mr-2" />
              Sign Up & Connect
            </Button>
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={handleLogin}
            >
              <LogIn className="h-4 w-4 mr-2" />
              Already Have an Account? Log In
            </Button>
          </div>

          {/* Trade Loop branding */}
          <div className="mt-8 pt-6 border-t w-full text-center">
            <p className="text-xs text-muted-foreground">
              Trade Loop - Professional Referral Network
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectPage;
